import { useStores } from '@/hooks/use-stores';
import React from 'react';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import { message, Modal, theme } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

const { useToken } = theme;

const PIDModal = observer(() => {
  const { pidEditStore } = useStores();
  const { activeMenu } = pidEditStore;
  const { token } = useToken();
  const { t } = useTranslation();

  let modalTitle: React.ReactNode = '';
  let modalContent: React.ReactNode = '';
  let confirmText = 'OK';
  let confirmButtonProps = {};

  if (activeMenu?.type === MenuType.DeleteDevice || activeMenu?.type === MenuType.DeleteOnDevice) {
    modalTitle = (
      <span>
        <ExclamationCircleFilled style={{ color: token.colorWarning, marginRight: token.marginXS }} />
        {t('Remove this asset?')}
      </span>
    );
    modalContent = t(
      "This will remove the selected asset from the map and its on-devices. This action can't be undone.",
    );
    confirmText = t('Remove Asset');
    confirmButtonProps = { danger: true };
  } else if (activeMenu?.type === MenuType.SaveChanges) {
    modalTitle = t('Apply Changes to P&ID?');
    modalContent = t(
      "These changes will be saved and added to the station's P&ID. This action may affect system configuration and visibility.",
    );
    confirmText = t('Confirm and Save');
  }

  const handleDeleteDevice = async () => {
    pidEditStore.hideMenu();
    if (!activeMenu?.context?.entity?.deviceId) return;

    try {
      await pidEditStore.deleteDevice(activeMenu.context.entity);
      message.success(t('Device deleted successfully'));
    } catch (error) {
      message.error(`${t('Failed to delete device')}: ${error.message || JSON.stringify(error)}`);
    }
  };

  const handleSaveChanges = async () => {
    pidEditStore.hideMenu();
    try {
      await pidEditStore.activateDraft();
      message.success(t('Draft Activated successfully'));

      await pidEditStore.disableEditModeLock();
      pidEditStore.setIsInEditMode(false);
      pidEditStore.toggleEditMode(false);
      message.info(t('Edit mode disabled.'));
    } catch (error) {
      message.error(`${t('Failed to save changes')}: ${error.message || JSON.stringify(error)}`);
    }
  };

  const handleConfirm = async () => {
    if (!activeMenu) return;

    switch (activeMenu.type) {
      case MenuType.DeleteDevice:
        await handleDeleteDevice();
        break;
      case MenuType.SaveChanges:
        await handleSaveChanges();
        break;
      default:
        pidEditStore.hideMenu();
        break;
    }
  };

  return (
    <Modal
      title={modalTitle}
      open={activeMenu?.type === MenuType.DeleteDevice || activeMenu?.type === MenuType.SaveChanges}
      onCancel={() => pidEditStore.hideMenu()}
      onOk={handleConfirm}
      okText={confirmText}
      okButtonProps={confirmButtonProps}
      cancelButtonProps={{
        danger: true,
      }}
    >
      {modalContent}
    </Modal>
  );
});

export default PIDModal;
